import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import { useAuth } from '../context/AuthContext';
import { FaExclamationTriangle, FaSpinner } from 'react-icons/fa';

/**
 * BillingRedirectPage
 *
 * Landing page that the "Update payment method" link in transactional emails
 * points to. It:
 *   1. Waits for AuthContext to finish loading
 *   2. If not authenticated → redirects to /login?next=/billing so the user
 *      logs in and returns automatically
 *   3. If authenticated → POSTs /api/billing/portal, then redirects to the
 *      Stripe Customer Portal URL returned by the backend
 *   4. Surfaces a clear error if the user has no stripe_customer_id yet
 */
const BillingRedirectPage = () => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [error, setError] = useState(null);

  useEffect(() => {
    if (loading) return;
    if (!user) {
      navigate('/login?next=/billing', { replace: true });
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.post(`${API}/billing/portal`, {}, { withCredentials: true });
        if (cancelled) return;
        if (r.data?.url) {
          window.location.replace(r.data.url);
        } else {
          setError('Kunne ikke åbne betalingsportalen — prøv igen senere.');
        }
      } catch (e) {
        if (cancelled) return;
        setError(e.response?.data?.detail || 'Kunne ikke åbne betalingsportalen.');
      }
    })();
    return () => { cancelled = true; };
  }, [user, loading, navigate]);

  return (
    <div className="min-h-[60vh] flex items-center justify-center p-6" data-testid="billing-redirect-page">
      <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md w-full text-center">
        {!error ? (
          <>
            <FaSpinner className="animate-spin text-4xl text-cyan-500 mx-auto mb-4" />
            <h1 className="text-xl font-bold text-gray-900 mb-2">Sender dig til betalingsportalen…</h1>
            <p className="text-sm text-gray-500">Her kan du opdatere kort, se kvitteringer og administrere dit abonnement.</p>
          </>
        ) : (
          <>
            <FaExclamationTriangle className="text-4xl text-red-500 mx-auto mb-4" />
            <h1 className="text-xl font-bold text-gray-900 mb-2">Vi kunne ikke åbne portalen</h1>
            <p className="text-sm text-gray-600 mb-5">{error}</p>
            <button
              onClick={() => navigate('/settings')}
              className="px-5 py-2.5 bg-gradient-to-r from-cyan-500 to-purple-500 text-white font-semibold rounded-lg hover:from-cyan-600 hover:to-purple-600"
              data-testid="billing-back-to-settings-btn"
            >
              Tilbage til indstillinger
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default BillingRedirectPage;
